/**
 * Which projects the shell lists, and which one it reads.
 *
 * The site Worker lists the signed-in account's projects on `/auth/session`
 * (`projects: [{ scope, name, role }]`) and reads `/v1/` only for one of them,
 * named as `?project=`. An authority served without the site in front of it
 * lists nothing and serves the one project its snapshot names; that is the
 * `null` directory, and the snapshot is its only row.
 */

import type { SurfaceSnapshot } from '../generated/surface.js';
import type { ProjectMembership } from './account-session.js';

/** The account's projects, or null where the origin lists none. */
export type ProjectDirectory = readonly ProjectMembership[] | null;

/** One project the shell lists, with the projection it holds for it, if any. */
export type ProjectEntry = {
  scope: string;
  name: string;
  /** The account's role on the project; null where the origin names no membership. */
  role: string | null;
  /** The snapshot in hand when it is this project's; one project is read at a time. */
  reading: SurfaceSnapshot | null;
};

/**
 * Every project to list. A membership is listed whether or not it has been
 * read; only the project the snapshot is of carries a reading, so nothing
 * from one project is shown as another's.
 */
export function projectEntries(directory: ProjectDirectory, snapshot: SurfaceSnapshot | null): ProjectEntry[] {
  if (directory === null) {
    return snapshot === null ? [] : [{ scope: snapshot.product, name: snapshot.product, role: null, reading: snapshot }];
  }
  return directory.map((membership) => ({
    scope: membership.scope,
    name: membership.name,
    role: membership.role,
    reading: snapshot !== null && snapshot.product === membership.scope ? snapshot : null,
  }));
}

/**
 * The project the shell reads.
 *
 * A `/p/<project>/…` address names it. Elsewhere the project last read stays
 * read while the account is still a member of it, else the account's first
 * project. With no directory the origin chooses, and with an empty one there
 * is nothing to read: every read would be refused `no_project`.
 */
export function viewedProject(
  routeProject: string | null,
  directory: ProjectDirectory,
  previous: string | null,
): string | null {
  if (routeProject !== null) return routeProject;
  if (directory === null) return previous;
  if (previous !== null && directory.some((membership) => membership.scope === previous)) return previous;
  return directory[0]?.scope ?? null;
}
